import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { CreateBookingRequest, CreateBookingResponse, BookingFormData } from '../types/api';
import { SERVICE_TYPES, ServiceType } from '../types/appointment';
import { parseTimeStringLA, APP_TIMEZONE } from '../utils/timezone';
import { availabilityService } from './availabilityService';

class BookingService {
  // Cloud Function reference
  private createBookingFunction = httpsCallable<CreateBookingRequest, CreateBookingResponse>(
    functions, 
    'bookAppointment'
  );

  /**
   * Submit a booking appointment
   */
  async createBooking(formData: BookingFormData): Promise<CreateBookingResponse> {
    try {
      // Validate form data
      this.validateBookingForm(formData);

      // Check if the time slot is still available (both disabled slots and existing appointments)
      const isSlotAvailable = await availabilityService.isTimeSlotAvailable(formData.selectedDate!, formData.selectedTime);
      if (!isSlotAvailable) {
        return {
          success: false,
          appointmentId: '',
          error: 'The selected time slot is no longer available. Please choose a different time.',
        };
      }

      // Convert form data to API request format
      const request: CreateBookingRequest = {
        customerInfo: {
          firstName: formData.firstName.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone?.trim(),
          wechatId: formData.wechatId?.trim(),
          message: formData.message?.trim() || '',
          uploadedFiles: formData.uploadedFiles || [], // Use the files array directly
        },
        serviceType: formData.selectedService,
        scheduledDateTime: this.combineDateTime(formData.selectedDate!, formData.selectedTime),
        timezone: APP_TIMEZONE, // Always use America/Los_Angeles timezone
        duration: formData.duration || 15, // Include duration from form data
        preferences: {
          addToCalendar: formData.addToCalendar,
          smsReminder: formData.smsReminder,
          communicationChannel: formData.meetingMethod, // Use the selected meeting method
        },
      };

      // Call the Cloud Function
      const result = await this.createBookingFunction(request);
      
      return result.data;
    } catch (error: any) {
      console.error('Booking creation failed:', error);
      
      // Handle different types of errors
      if (error.code === 'functions/invalid-argument') {
        return {
          success: false,
          appointmentId: '',
          error: 'Invalid booking information provided. Please check your details and try again.',
        };
      }
      
      if (error.code === 'functions/unavailable') {
        return {
          success: false,
          appointmentId: '',
          error: 'Service temporarily unavailable. Please try again in a few minutes.',
        };
      }

      return {
        success: false,
        appointmentId: '',
        error: error.message || 'An unexpected error occurred. Please try again.',
      };
    }
  }

  /**
   * Validate booking form data
   */
  private validateBookingForm(formData: BookingFormData): void {
    const errors: string[] = [];

    // Required fields
    if (!formData.firstName?.trim()) {
      errors.push('First name is required');
    }

    if (!formData.email?.trim()) {
      errors.push('Email is required');
    } else if (!this.isValidEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!formData.selectedService || !Object.values(SERVICE_TYPES).includes(formData.selectedService as ServiceType)) {
      errors.push('Please select a service');
    }

    if (!formData.selectedDate) {
      errors.push('Please select a date');
    } else {
      // Check if date is in the future
      const selectedDateTime = new Date(formData.selectedDate);
      const now = new Date();
      if (selectedDateTime <= now) {
        errors.push('Please select a future date');
      }
    }

    if (!formData.selectedTime?.trim()) {
      errors.push('Please select a time');
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }
  }

  /**
   * Simple email validation
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get available time slots for a given date
   * This could be expanded to call a backend function for dynamic availability
   */
  getAvailableTimeSlots(date: Date): string[] {
    const dayOfWeek = date.getDay();
    
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // Weekends - limited hours
      return ['10:00 AM', '10:30 AM', '11:00 AM', '2:00 PM', '2:30 PM'];
    }
    
    // Weekdays - full schedule
    return [
      '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM',
      '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM'
    ];
  }

  /**
   * Combine date and time into a timezone-aware ISO string (LA timezone)
   */
  private combineDateTime(date: Date, time: string): string {
    console.group('🕐 Combining Date & Time');
    console.log('Input date:', date.toDateString());
    console.log('Input time:', time);
    
    // Parse the time in LA timezone and combine with date
    const combinedDate = parseTimeStringLA(date, time);
    
    console.log('Combined result (toString):', combinedDate.toString());
    console.log('Combined result (ISO/UTC):', combinedDate.toISOString());
    console.log('Display in LA timezone:', combinedDate.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' }));
    console.log('Display in NY timezone:', combinedDate.toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' }));
    console.log('UTC Hours:', combinedDate.getUTCHours());
    console.groupEnd();
    
    return combinedDate.toISOString();
  }

  /**
   * Check if a specific date/time slot is available
   * Now properly implemented using the availability service
   */
  async isSlotAvailable(date: Date, time: string): Promise<boolean> {
    return availabilityService.isTimeSlotAvailable(date, time);
  }
}

// Export a singleton instance
export const bookingService = new BookingService();
export default bookingService;
